import React from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { useActiveTenant } from './TenantSwitcher'
import { TenantGate } from './TenantGate'

/**
 * Minimal wrapper for screens reached via a hand-off from another app (e.g.
 * social's "Activate" hand-off): no header, no side nav — just the page's
 * own content, centered, matching the login page's minimalism. The user in
 * this flow may have no ability to navigate the wider admin UI at all, so
 * nothing that implies "here's a whole app to explore" should render.
 *
 * Gates children behind tenant resolution via the shared TenantGate
 * component (./TenantGate.tsx), mirroring AdminLayout's own gate. For a
 * system admin with no tenant claim and no persisted selection,
 * useActiveTenant()'s tenant starts as "" and only resolves asynchronously
 * once TenantProvider's adminListTenants() call returns. Originally
 * reported bug: right after logging in — landing here via a client-side
 * navigation from /admin/callback, in the SAME React tree, so that async
 * resolution hasn't caught up yet — a page rendered with tenant === ""
 * mounted its fetch with an empty tenantId. The generated client turns that
 * into a request path with a literal double slash
 * (/v1/admin/tenants//access-requests/...); Go's mux 307-redirects that to
 * a SHORTER path missing the tenant segment entirely, which matches no
 * real API route and falls through to the SPA's own catch-all — a 200 OK
 * HTML body where JSON was expected. A subsequent full page reload doesn't
 * hit this (tenant is by then already persisted in localStorage from the
 * earlier resolution and resolves synchronously on mount), which is why
 * the bug only ever showed up right after login, never after a refresh.
 *
 * Follow-up fix (291c525f fixed AdminLayout's version of this same bug but
 * left this layout's copy unfixed): TenantGate renders the spinner below
 * ONLY while a resolution attempt (claims fallback or refreshTenants()) is
 * genuinely in flight. Once resolution is exhausted —
 * useActiveTenant().tenantError is non-null, e.g. a system admin with no
 * tenant claim whose adminListTenants() call failed — it renders an
 * accessible `role="alert"` message instead, so a user for whom the tenant
 * genuinely cannot be resolved is never left staring at an endless spinner.
 * See TenantGate's own doc comment for the full state contract.
 *
 * All theme lookups below use `ut(asUnified(theme))` (space) with a literal
 * fallback, so this component never throws in a bare test render (no
 * styled-components ThemeProvider required) — TenantGate degrades the same
 * way.
 */
const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => ut(asUnified(theme)).space('xl')};
`

const Content = styled.div`
  width: 100%;
  max-width: 720px;
`

export const BareLayout = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { tenant, tenantError } = useActiveTenant()
  return (
    <Shell>
      <Content>
        {tenant === '' ? (
          // Active tenant not yet resolved. Hold children so they don't
          // mount + fire requests with an empty tenantId (see doc comment
          // above). TenantGate (./TenantGate.tsx, shared with AdminLayout)
          // distinguishes a genuinely in-flight resolution attempt
          // (spinner) from an exhausted one (accessible alert).
          <TenantGate tenantError={tenantError ?? null} />
        ) : (
          children
        )}
      </Content>
    </Shell>
  )
}
